import React, { useEffect, useState } from 'react';
import { FaApple, FaWindows } from 'react-icons/fa';
import { useLanguageContext } from '../contexts/LanguageContext';

type Platform = 'mac' | 'windows';

const platformToApi: Record<Platform, 'macos-universal' | 'windows-x64'> = {
  mac: 'macos-universal',
  windows: 'windows-x64',
};

const allPlatforms: Platform[] = ['mac', 'windows'];

function versionLabel(version: string | undefined): string {
  if (!version) return '';
  return version.startsWith('v') ? version : `v${version}`;
}

function ButtonFace({ platform, label, version, large }: { platform: Platform; label: string; version?: string; large: boolean }) {
  const shown = versionLabel(version);
  return (
    <div className={`flex flex-col items-center justify-center ${large ? 'h-[96px]' : 'h-[72px]'}`}>
      <span className={`inline-flex items-center justify-center gap-3 whitespace-nowrap ${large ? 'text-3xl' : 'text-2xl'}`}>
        <span className={large ? 'relative -top-[1px] md:-top-[2px]' : 'relative -top-[1px]'}>
          {platform === 'mac' ? <FaApple size={large ? 32 : 28} /> : <FaWindows size={large ? 32 : 28} />}
        </span>
        {label}
      </span>
      <span className={`mt-1 font-normal leading-none tracking-wide text-white/65 ${large ? 'text-xs' : 'text-[11px]'} ${shown ? '' : 'invisible'}`}>
        {shown || 'v0.0.0'}
      </span>
    </div>
  );
}

const Downloads: React.FC = () => {
  const { t } = useLanguageContext();
  const [versions, setVersions] = useState<Partial<Record<Platform, string>>>({});
  const detectPlatform = (): Platform => {
    const ua = (navigator.userAgent || navigator.platform || '').toLowerCase();
    
    if (ua.includes('win')) return 'windows';
    if (ua.includes('mac') || ua.includes('darwin')) return 'mac';
    return 'mac';
  };
  const platform: Platform = detectPlatform();

  const otherPlatforms: Platform[] = allPlatforms.filter((p) => p !== platform);

  const labels: Record<Platform, string> = {
    mac: t('downloads.download_mac') || 'Download macOS',
    windows: t('downloads.download_windows') || 'Download Windows',
  };

  useEffect(() => {
    let cancelled = false;
    Promise.all(allPlatforms.map(async (p) => {
      try {
        const response = await fetch(`/api/installer/channel?platform=${encodeURIComponent(platformToApi[p])}`);
        if (!response.ok) return [p, ''] as const;
        const data = await response.json();
        const current = typeof data.current === 'string' ? data.current.trim() : '';
        return [p, current] as const;
      } catch {
        return [p, ''] as const;
      }
    })).then((entries) => {
      if (cancelled) return;
      const next: Partial<Record<Platform, string>> = {};
      entries.forEach(([p, current]) => {
        if (current) next[p] = current;
      });
      setVersions(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const startDownload = async (p: Platform) => {
    try {
      const apiPlat = platformToApi[p];
      const ch = await fetch(`/api/installer/channel?platform=${encodeURIComponent(apiPlat)}`);
      if (!ch.ok) throw new Error('channel-failed');
      const cj = await ch.json();
      const version = cj.current as string;

      const rel = await fetch(`/api/installer/release?platform=${encodeURIComponent(apiPlat)}&version=${encodeURIComponent(version)}`);
      if (!rel.ok) throw new Error('release-failed');
      const rj = await rel.json();
      const url = rj.downloadUrl as string;
      if (!url) throw new Error('no-url');
      // Navigate to the short‑lived signed URL
      window.location.href = url;
    } catch {
      // Optionally show a toast; for now do nothing
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <h1 className="text-3xl font-bold text-white mb-6">{t('downloads.title') || 'Downloads'}</h1>

      {/* Hero with installer preview and CTAs */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.8fr_1fr] gap-8 items-center mb-10">
        <div className="w-full">
          <img
            src="https://sonicmakers-uploads.lon1.digitaloceanspaces.com/public/images/sonicmakers-installer-preview.gif"
            alt={t('downloads.preview_alt') || 'Sonic Makers Installer preview'}
            className="w-full h-auto object-contain"
            loading="lazy"
          />
        </div>
        <div>
          <h2 className="text-3xl font-bold text-white mb-4">{t('downloads.installer_title') || 'Sonic Makers Installer'}</h2>
          <p className="text-gray-300 mb-6">{t('downloads.installer_desc') || 'Download, install, activate and update SONIC MAKERS products from one app.'}</p>
          <button
            onClick={() => startDownload(platform)}
            className="block w-full text-center px-6 py-0 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-semibold shadow"
          >
            <ButtonFace platform={platform} label={labels[platform]} version={versions[platform]} large />
          </button>
          <div className="mt-4 space-y-2 w-5/6 mx-auto">
            {otherPlatforms.map((p) => (
              <button
                key={p}
                onClick={() => startDownload(p)}
                className="block w-full text-center px-6 py-0 rounded-lg bg-slate-700 hover:bg-slate-600 text-white"
              >
                <ButtonFace platform={p} label={labels[p]} version={versions[p]} large={false} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Downloads;


